import { z } from "zod";

/**
 * Metered resources, as a **registry** rather than a fixed set of columns.
 *
 * `06-saas-admin.md` § 3 finding 1 is the reason. The live runtime probe
 * exposed three — `pos_menu_items`, `employee`, `table` — but the published price list
 * also meters customers and suppliers, so the set is larger than one probe
 * showed and will grow again. Adding a resource must be a row and a registry
 * entry, never a migration.
 *
 * The item quota counts regular + pre-order + temporary items together
 * (`07-entitlements-rbac.md` § 2). Copy that definition exactly, or merchants
 * will dispute their counts.
 */
type QuotaDefinition = {
  readonly resource: string;
  readonly name: string;
  /** What the count includes, where that is not obvious from the name. */
  readonly countedAs: string;
};

const DEFINITIONS = [
  {
    resource: "item",
    name: "Items",
    countedAs: "regular, pre-order and temporary items together",
  },
  {
    resource: "employee",
    name: "Staff",
    countedAs: "active accounts that reach the store, owners and chain accounts included",
  },
  { resource: "table", name: "Tables", countedAs: "tables across all areas" },
  { resource: "customer", name: "Customers", countedAs: "members registered to the store" },
  { resource: "supplier", name: "Suppliers", countedAs: "inventory suppliers" },
  {
    resource: "store",
    name: "Stores",
    countedAs: "locations under the chain, the head office's own store included",
  },
] as const satisfies readonly QuotaDefinition[];

export type QuotaResource = (typeof DEFINITIONS)[number]["resource"];

export const QUOTA_CATALOG: readonly QuotaDefinition[] = DEFINITIONS;

export const QUOTA_RESOURCES = DEFINITIONS.map((q) => q.resource) as unknown as readonly [
  QuotaResource,
  ...QuotaResource[],
];

const BY_RESOURCE = new Map<string, QuotaDefinition>(DEFINITIONS.map((q) => [q.resource, q]));

export function isQuotaResource(value: string): value is QuotaResource {
  return BY_RESOURCE.has(value);
}

export function quotaDefinition(resource: QuotaResource): QuotaDefinition {
  const found = BY_RESOURCE.get(resource);
  if (!found) throw new Error(`Unknown quota resource: ${resource}`);
  return found;
}

export const EXTRA_PURCHASE_MODES = ["DISABLED", "SELF_SERVICE", "CONTACT_SALES"] as const;
export type ExtraPurchaseMode = (typeof EXTRA_PURCHASE_MODES)[number];

/**
 * What a plan sold. Snapshotted onto the subscription so a catalog edit cannot
 * retroactively change what a merchant already paid for.
 *
 * **`null` means unmetered**, and it is not a sentinel like 99,999
 * (`06-saas-admin.md` § 3, Seed values). The source uses 99,999 on its own
 * enterprise account, and a magic number a merchant can theoretically exceed is
 * a bug waiting for the day someone has 100,000 items.
 */
export const QuotaAllowanceSchema = z.object({
  limit: z.number().int().nonnegative().nullable(),
  extra: z.number().int().nonnegative(),
  allowed: z.number().int().nonnegative().nullable(),
});

export type QuotaAllowance = z.infer<typeof QuotaAllowanceSchema>;

/** The allowance plus live consumption, as `/me` and the quota endpoint return it. */
export const QuotaStateSchema = QuotaAllowanceSchema.extend({
  used: z.number().int().nonnegative(),
  /** Remaining headroom. `null` when unmetered — there is no headroom to count. */
  over: z.number().int().nullable(),
  allowExtra: z.boolean(),
  extraPurchaseMode: z.enum(EXTRA_PURCHASE_MODES),
});

export type QuotaState = z.infer<typeof QuotaStateSchema>;

/** A snapshot covers every registered resource, so a later registry addition is visible as absent. */
export const QuotaSnapshotSchema = z.record(z.enum(QUOTA_RESOURCES), QuotaAllowanceSchema);
export type QuotaSnapshot = z.infer<typeof QuotaSnapshotSchema>;

export function unmetered(): QuotaAllowance {
  return { limit: null, extra: 0, allowed: null };
}

export function metered(limit: number, extra = 0): QuotaAllowance {
  return { limit, extra, allowed: limit + extra };
}

export function quotaState(
  allowance: QuotaAllowance,
  used: number,
  options: { allowExtra?: boolean; extraPurchaseMode?: ExtraPurchaseMode } = {},
): QuotaState {
  return {
    ...allowance,
    used,
    over: allowance.allowed === null ? null : allowance.allowed - used,
    allowExtra: options.allowExtra ?? false,
    extraPurchaseMode: options.extraPurchaseMode ?? "DISABLED",
  };
}

/** Whether one more of this resource may be created. Unmetered always may. */
export function withinQuota(allowance: QuotaAllowance, used: number): boolean {
  return allowance.allowed === null || used < allowance.allowed;
}
